import { useEffect, useRef } from 'react';

import { pointerMapMidpoint, touchListMidpoint } from './ttqibFocusViewport';
import { TTQIB_ZOOM_MAX, TTQIB_ZOOM_MIN } from './ttqibViewport';
import { ttqCanvasGesture } from './ttqCanvasGesture';
import type { TtqibViewport } from './types';

type MultiPanState = {
  startMidX: number;
  startMidY: number;
  originX: number;
  originY: number;
};

type PinchState = {
  startDist: number;
  startZoom: number;
  originX: number;
  originY: number;
};

type UseTtqCanvasMultiTouchPanOptions = {
  containerRef: React.RefObject<HTMLElement | null>;
  vpRef: React.MutableRefObject<TtqibViewport>;
  onViewportChange: ((viewport: TtqibViewport) => void) | undefined;
  /** 开始多指手势时取消单指平移，并清掉误触的选段 / 底栏 */
  onMultiPanStart?: () => void;
  enabled?: boolean;
};

const PAN_TOUCH_MIN = 3;

function pointerMapDistance(pointers: Map<number, { x: number; y: number }>): number {
  if (pointers.size < 2) return 0;
  const pts = [...pointers.values()];
  return Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
}

function touchListDistance(touches: TouchList): number {
  if (touches.length < 2) return 0;
  const a = touches[0];
  const b = touches[1];
  return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
}

function applyZoomAround(
  change: (viewport: TtqibViewport) => void,
  pinch: PinchState,
  localFocus: { x: number; y: number },
  dist: number,
): void {
  if (dist < 8) return;
  const ratio = dist / pinch.startDist;
  const nextZoom = Math.min(
    TTQIB_ZOOM_MAX,
    Math.max(TTQIB_ZOOM_MIN, pinch.startZoom * ratio),
  );
  const worldX = (localFocus.x - pinch.originX) / pinch.startZoom;
  const worldY = (localFocus.y - pinch.originY) / pinch.startZoom;
  change({
    x: localFocus.x - worldX * nextZoom,
    y: localFocus.y - worldY * nextZoom,
    zoom: nextZoom,
  });
}

/**
 * 画布多指手势：
 * - 2 指：捏合缩放（绕双指中点），并拦住浏览器页级缩放（避免视口甩向左上角）
 * - ≥3 指：平移
 */
export function useTtqCanvasMultiTouchPan({
  containerRef,
  vpRef,
  onViewportChange,
  onMultiPanStart,
  enabled = true,
}: UseTtqCanvasMultiTouchPanOptions) {
  const multiPanRef = useRef<MultiPanState | null>(null);
  const pinchRef = useRef<PinchState | null>(null);
  const activePointersRef = useRef(new Map<number, { x: number; y: number }>());
  const onViewportChangeRef = useRef(onViewportChange);
  const onMultiPanStartRef = useRef(onMultiPanStart);
  onViewportChangeRef.current = onViewportChange;
  onMultiPanStartRef.current = onMultiPanStart;

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return;

    const syncGestureCount = () => {
      ttqCanvasGesture.setActivePointers(activePointersRef.current.size);
    };

    const blockUiEvent = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };

    const toLocal = (clientX: number, clientY: number) => {
      const rect = el.getBoundingClientRect();
      return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const beginPanFromMid = (mid: { x: number; y: number }) => {
      const already = Boolean(multiPanRef.current);
      const vp = vpRef.current;
      pinchRef.current = null;
      multiPanRef.current = {
        startMidX: mid.x,
        startMidY: mid.y,
        originX: vp.x,
        originY: vp.y,
      };
      ttqCanvasGesture.setMultiPanActive(true);
      if (!already) onMultiPanStartRef.current?.();
    };

    const applyPanFromMid = (mid: { x: number; y: number }) => {
      const pan = multiPanRef.current;
      const change = onViewportChangeRef.current;
      if (!pan || !change) return;
      change({
        ...vpRef.current,
        x: pan.originX + (mid.x - pan.startMidX),
        y: pan.originY + (mid.y - pan.startMidY),
      });
    };

    const beginPinch = (clientMid: { x: number; y: number }, dist: number) => {
      if (dist < 8) return;
      const already = Boolean(pinchRef.current);
      const vp = vpRef.current;
      multiPanRef.current = null;
      pinchRef.current = {
        startDist: dist,
        startZoom: vp.zoom,
        originX: vp.x,
        originY: vp.y,
      };
      ttqCanvasGesture.setMultiPanActive(true);
      if (!already) onMultiPanStartRef.current?.();
      void clientMid;
    };

    const reanchorIfNeeded = () => {
      syncGestureCount();
      const count = activePointersRef.current.size;
      if (count === 0) {
        multiPanRef.current = null;
        pinchRef.current = null;
        ttqCanvasGesture.setMultiPanActive(false);
        return;
      }
      if (count >= PAN_TOUCH_MIN) {
        beginPanFromMid(pointerMapMidpoint(activePointersRef.current));
        return;
      }
      if (count === 2) {
        const mid = pointerMapMidpoint(activePointersRef.current);
        beginPinch(mid, pointerMapDistance(activePointersRef.current));
        return;
      }
      multiPanRef.current = null;
      pinchRef.current = null;
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      syncGestureCount();

      if (activePointersRef.current.size >= 2) blockUiEvent(event);

      if (activePointersRef.current.size >= PAN_TOUCH_MIN) {
        beginPanFromMid(pointerMapMidpoint(activePointersRef.current));
        return;
      }
      if (activePointersRef.current.size === 2) {
        const mid = pointerMapMidpoint(activePointersRef.current);
        beginPinch(mid, pointerMapDistance(activePointersRef.current));
      }
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!activePointersRef.current.has(event.pointerId)) return;
      activePointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const count = activePointersRef.current.size;
      const change = onViewportChangeRef.current;

      if (count >= PAN_TOUCH_MIN) {
        if (!multiPanRef.current) {
          beginPanFromMid(pointerMapMidpoint(activePointersRef.current));
        }
        blockUiEvent(event);
        applyPanFromMid(pointerMapMidpoint(activePointersRef.current));
        return;
      }

      if (count === 2 && change) {
        blockUiEvent(event);
        const mid = pointerMapMidpoint(activePointersRef.current);
        const dist = pointerMapDistance(activePointersRef.current);
        if (!pinchRef.current) {
          beginPinch(mid, dist);
          return;
        }
        applyZoomAround(change, pinchRef.current, toLocal(mid.x, mid.y), dist);
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      activePointersRef.current.delete(event.pointerId);
      reanchorIfNeeded();
    };

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length >= 2) {
        ttqCanvasGesture.setActivePointers(Math.max(ttqCanvasGesture.activePointers, event.touches.length));
        blockUiEvent(event);
      }
      if (event.touches.length >= PAN_TOUCH_MIN) {
        beginPanFromMid(touchListMidpoint(event.touches));
        return;
      }
      if (event.touches.length === 2) {
        beginPinch(touchListMidpoint(event.touches), touchListDistance(event.touches));
      }
    };

    const onTouchMove = (event: TouchEvent) => {
      const change = onViewportChangeRef.current;
      if (event.touches.length >= PAN_TOUCH_MIN) {
        if (!multiPanRef.current) {
          beginPanFromMid(touchListMidpoint(event.touches));
        }
        blockUiEvent(event);
        applyPanFromMid(touchListMidpoint(event.touches));
        return;
      }
      if (event.touches.length === 2 && change) {
        blockUiEvent(event);
        const mid = touchListMidpoint(event.touches);
        const dist = touchListDistance(event.touches);
        if (!pinchRef.current) {
          beginPinch(mid, dist);
          return;
        }
        applyZoomAround(change, pinchRef.current, toLocal(mid.x, mid.y), dist);
        return;
      }
      multiPanRef.current = null;
      pinchRef.current = null;
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (event.touches.length >= PAN_TOUCH_MIN) {
        beginPanFromMid(touchListMidpoint(event.touches));
        return;
      }
      if (event.touches.length === 2) {
        beginPinch(touchListMidpoint(event.touches), touchListDistance(event.touches));
        return;
      }
      multiPanRef.current = null;
      pinchRef.current = null;
      if (event.touches.length === 0) {
        activePointersRef.current.clear();
        ttqCanvasGesture.setActivePointers(0);
        ttqCanvasGesture.setMultiPanActive(false);
      }
    };

    const onClickCapture = (event: MouseEvent) => {
      if (!ttqCanvasGesture.shouldBlockClick()) return;
      blockUiEvent(event);
    };

    el.addEventListener('pointerdown', onPointerDown, { capture: true });
    el.addEventListener('pointermove', onPointerMove, { capture: true });
    el.addEventListener('pointerup', onPointerUp, { capture: true });
    el.addEventListener('pointercancel', onPointerUp, { capture: true });
    el.addEventListener('touchstart', onTouchStart, { capture: true, passive: false });
    el.addEventListener('touchmove', onTouchMove, { capture: true, passive: false });
    el.addEventListener('touchend', onTouchEnd, { capture: true, passive: false });
    el.addEventListener('touchcancel', onTouchEnd, { capture: true, passive: false });
    el.addEventListener('click', onClickCapture, { capture: true });

    return () => {
      el.removeEventListener('pointerdown', onPointerDown, true);
      el.removeEventListener('pointermove', onPointerMove, true);
      el.removeEventListener('pointerup', onPointerUp, true);
      el.removeEventListener('pointercancel', onPointerUp, true);
      el.removeEventListener('touchstart', onTouchStart, true);
      el.removeEventListener('touchmove', onTouchMove, true);
      el.removeEventListener('touchend', onTouchEnd, true);
      el.removeEventListener('touchcancel', onTouchEnd, true);
      el.removeEventListener('click', onClickCapture, true);
      activePointersRef.current.clear();
      multiPanRef.current = null;
      pinchRef.current = null;
      ttqCanvasGesture.setActivePointers(0);
      ttqCanvasGesture.setMultiPanActive(false);
    };
  }, [containerRef, enabled, vpRef]);
}
